import { useState, useEffect, useRef, useCallback } from 'react';
import { getMockViolationsOverTime } from '../mock/tradingRulesMetricsMock';
import { resolveTradingRulesMockScenario } from '../utils/tradingRulesMockScenario';
import { aggregateViolationsOverTime } from '../utils/tradingRulesViolationBuckets';
import useAPI from './useApi';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

/**
 * Hook to fetch violations over time data for charting
 * 
 * Uses mock data when v= query parameter is present, otherwise uses real API.
 * Aggregates violations from /trading_rules_violations into time buckets on frontend.
 * 
 * @param {Object} account - Selected account object
 * @param {string} timeRange - 'today', '7days', or '30days'
 * @param {Object} options - Aggregation options ({ includePlanDeviations: boolean })
 * @returns {Object} { data, loading, error }
 */
const useTradingRulesViolationsOverTime = (account, timeRange = 'today', options = {}) => {
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const { request } = useAPI();
  const isMountedRef = useRef(true);
  const isInitialLoadRef = useRef(true);
  const requestIdRef = useRef(0);
  // Avoid object-identity dependency churn when callers omit options (default `{}` per render).
  const includePlanDeviations = options?.includePlanDeviations === true;

  const fetchData = useCallback(async (isRefresh = false) => {
    if (!isMountedRef.current) return;
    const requestId = ++requestIdRef.current;
    const stillCurrent = () => isMountedRef.current && requestId === requestIdRef.current;
    
    // Only show loading spinner on initial load, not on refresh
    if (!isRefresh && isInitialLoadRef.current) {
      setLoading(true);
    }
    setError(null);

    try {
      const { useMock, scenarioVersion } = resolveTradingRulesMockScenario();

      if (useMock) {
        // Mock mode: Simulate API delay and use mock data with scenario support
        await new Promise(resolve => setTimeout(resolve, 300));

        // Use mock data with scenario if provided
        const mockData = getMockViolationsOverTime(timeRange, scenarioVersion);
        if (stillCurrent()) {
          setData(mockData);
        }

        console.log('[useTradingRulesViolationsOverTime] Using mock data');
      } else {
        // Real API mode: Fetch violations and aggregate on frontend
        if (!account?.AccountID) {
          if (isMountedRef.current) {
            setLoading(false);
          }
          return;
        }

        const accountId = account.AccountID;

        console.log(`[useTradingRulesViolationsOverTime] Fetching from API: account=${accountId}, timeRange=${timeRange}`);

        const violationsResponse = await request(
          `${BACKEND_URL}/trading_rules_violations?account_id=${accountId}&time_range=${timeRange}`,
          {},
          'GET'
        );

        // Aggregate violations into time buckets
        const violations = violationsResponse?.violations || [];
        const aggregatedData = aggregateViolationsOverTime(violations, timeRange, { includePlanDeviations });
        
        if (!stillCurrent()) return;
        
        setData(aggregatedData);

        console.log('[useTradingRulesViolationsOverTime] Fetched and aggregated real data from API', {
          violationsCount: violations.length,
          aggregatedBuckets: aggregatedData.length
        });
      }

      if (stillCurrent()) {
        setLoading(false);
        isInitialLoadRef.current = false;
      }
    } catch (err) {
      console.error('[useTradingRulesViolationsOverTime] Error fetching data:', err);
      if (stillCurrent()) {
        setError(err.message || 'Failed to load violations data');
        setLoading(false);
        isInitialLoadRef.current = false;
      }
    }
  }, [account?.AccountID, includePlanDeviations, timeRange, request]);

  useEffect(() => {
    isMountedRef.current = true;
    isInitialLoadRef.current = true;

    // Only fetch if we have account and timeRange is valid (not null/undefined)
    if (account?.AccountID && timeRange !== null && timeRange !== undefined && timeRange !== '') {
      fetchData(false);
    } else {
      setLoading(false);
    }

    return () => {
      isMountedRef.current = false;
    };
  }, [account?.AccountID, timeRange, fetchData]);

  // Refetch function that doesn't show loading spinner
  const refetch = useCallback(() => {
    fetchData(true);
  }, [fetchData]);

  useEffect(() => {
    const handleViolationDeleted = () => refetch();
    window.addEventListener('tradingRules:violationDeleted', handleViolationDeleted);
    return () => window.removeEventListener('tradingRules:violationDeleted', handleViolationDeleted);
  }, [refetch]);

  return {
    data,
    loading,
    error,
    refetch
  };
};

export default useTradingRulesViolationsOverTime;
